import { Controller } from '@hotwired/stimulus';

// Contrôleur pour calculer automatiquement le % d'avancement depuis le montant HT et vice-versa
// Permet une saisie bidirectionnelle : soit % → montant HT, soit montant HT → %
export default class extends Controller {
  static targets = ['percentage', 'amount', 'info'];
  static values = {
    contractTotal: Number, // Total du contrat HT
    previousCumul: Number, // Cumul HT de la situation précédente
  };

  connect() {
    // Au chargement, ne PAS pré-remplir le champ montant automatiquement
    // L'utilisateur doit choisir explicitement quel champ utiliser
    // On ne calcule que si l'utilisateur interagit avec les champs
  }

  // Appelé quand l'utilisateur modifie le champ "Avancement (%)"
  // Calcule automatiquement le montant HT correspondant
  updateAmountFromPercentage() {
    const percentage = parseFloat(this.percentageTarget.value) || 0;
    const contractTotal = this.contractTotalValue;

    if (contractTotal === 0) return;

    // Calculer le cumul actuel HT = (pourcentage / 100) × total contrat
    const currentCumulHt = (percentage / 100) * contractTotal;

    // Calculer le montant de la période = cumul actuel - cumul précédent
    const periodAmount = currentCumulHt - this.previousCumulValue;

    // Mettre à jour le champ montant HT (arrondi à 2 décimales)
    if (this.hasAmountTarget) {
      this.amountTarget.value = periodAmount.toFixed(2);
    }

    // Mettre à jour l'indicateur visuel
    this.updateActiveIndicator('percentage');
  }

  // Appelé quand l'utilisateur modifie le champ "Montant HT de la situation"
  // Calcule automatiquement le % d'avancement correspondant
  updatePercentageFromAmount() {
    const periodAmount = parseFloat(this.amountTarget.value) || 0;
    const contractTotal = this.contractTotalValue;

    if (contractTotal === 0) return;

    // Calculer le nouveau cumul actuel = cumul précédent + montant de la période
    const newCumulHt = this.previousCumulValue + periodAmount;

    // Calculer le % correspondant = (cumul actuel / total contrat) × 100
    const percentage = (newCumulHt / contractTotal) * 100;

    // Limiter entre 0 et 100%
    const limitedPercentage = Math.max(0, Math.min(100, percentage));

    // Mettre à jour le champ pourcentage (6 décimales pour précision)
    if (this.hasPercentageTarget) {
      this.percentageTarget.value = limitedPercentage.toFixed(6);
    }

    // Mettre à jour l'indicateur visuel
    this.updateActiveIndicator('amount');
  }

  // Met à jour l'indicateur visuel pour montrer quel champ pilote le calcul
  updateActiveIndicator(activeField) {
    // Cibler directement les inputs via leurs data-attributes
    const percentageInput = this.element.querySelector('[data-field-type="percentage"]');
    const amountInput = this.element.querySelector('[data-field-type="amount"]');

    // Retirer les anciennes classes de tous les inputs
    percentageInput?.classList.remove('ring-2', 'ring-primary-500', 'ring-offset-2');
    amountInput?.classList.remove('ring-2', 'ring-primary-500', 'ring-offset-2');

    // Ajouter une bordure sur le champ actif (uniquement l'input)
    if (activeField === 'percentage' && percentageInput) {
      percentageInput.classList.add('ring-2', 'ring-primary-500', 'ring-offset-2');
    } else if (activeField === 'amount' && amountInput) {
      amountInput.classList.add('ring-2', 'ring-primary-500', 'ring-offset-2');
    }
  }

  // Formatte un nombre avec espaces comme séparateurs de milliers
  formatCurrency(value) {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
}
